import { formatDateTime } from '../shared/dateTimeFormatting';
import { useDateTimePreferences } from '../shared/DateTimeProvider';
import React from 'react';
import { Alert } from '../user-dashboard/UserDashboard';
import type { AlertWorkflowProgress, User } from '../services/tenantApi';
import { WorkflowProgressPanel, type WorkflowProgressSubscriber } from './WorkflowProgressPanel';
import { formatEventPayload, getWebhookImageArtifact } from './alertEventPayload';
import { EventPayloadModal } from './EventPayloadModal';
import './AlertDetail.css';

interface AlertDetailProps {
  alert: Alert;
  onAcknowledge: () => void;
  onClose: (resolutionNote?: string) => void;
  onReopen?: () => Promise<void>;
  onAddNote: (note: string) => void;
  onClosePanel: () => void;
  onChangePriority?: (priority: string) => void;
  onAssign?: (userId: string) => void;
  users?: User[];
  loadWorkflowProgress?: (alertId: string) => Promise<AlertWorkflowProgress>;
  subscribeWorkflowProgress?: WorkflowProgressSubscriber;
}

export const AlertDetail: React.FC<AlertDetailProps> = ({
  alert,
  onAcknowledge,
  onClose,
  onReopen,
  onAddNote,
  onClosePanel,
  onChangePriority,
  onAssign,
  users,
  loadWorkflowProgress,
  subscribeWorkflowProgress,
}) => {
  useDateTimePreferences();
  const [note, setNote] = React.useState('');
  const [resolutionNote, setResolutionNote] = React.useState('');
  const [showPriorityPicker, setShowPriorityPicker] = React.useState(false);
  const [showAssignPicker, setShowAssignPicker] = React.useState(false);
  const [showRawPayload, setShowRawPayload] = React.useState(false);
  const [eventPayload, setEventPayload] = React.useState<{
    payload: string;
    heading: string;
    trigger: HTMLButtonElement;
    imageArtifact?: ReturnType<typeof getWebhookImageArtifact>;
  } | null>(null);
  const [reopening, setReopening] = React.useState(false);
  const [reopenError, setReopenError] = React.useState('');
  const detailRef = React.useRef<HTMLDivElement | null>(null);

  // Escalation countdown
  const [secondsRemaining, setSecondsRemaining] = React.useState<number | null>(
    alert.escalationInfo?.secondsRemaining ?? null
  );

  React.useEffect(() => {
    const initial = alert.escalationInfo?.secondsRemaining ?? null;
    setSecondsRemaining(initial);
    if (initial == null || initial <= 0 || alert.escalationInfo?.isComplete) return;
    const interval = setInterval(() => {
      setSecondsRemaining((prev) => {
        if (prev == null || prev <= 1) {
          clearInterval(interval);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, [alert.alertId, alert.escalationInfo?.secondsRemaining, alert.escalationInfo?.isComplete]);

  React.useEffect(() => {
    const onClickAway = (evt: MouseEvent) => {
      if (!detailRef.current) return;
      if (detailRef.current.contains(evt.target as Node)) return;
      setShowPriorityPicker(false);
      setShowAssignPicker(false);
    };

    document.addEventListener('mousedown', onClickAway);
    return () => document.removeEventListener('mousedown', onClickAway);
  }, []);

  const handleAddNote = () => {
    if (note.trim()) {
      onAddNote(note);
      setNote('');
    }
  };

  const handleClose = () => {
    onClose(resolutionNote || undefined);
  };

  const handleReopen = async () => {
    if (!onReopen || reopening || !window.confirm('Reopen this closed alert?')) return;
    setReopening(true);
    setReopenError('');
    try {
      await onReopen();
    } catch {
      setReopenError('Failed to reopen alert. Refresh the alert and try again.');
    } finally {
      setReopening(false);
    }
  };

  const handlePriorityChange = (priority: string) => {
    setShowPriorityPicker(false);
    onChangePriority?.(priority);
  };

  const getSeverityClass = (severity: string) => `severity-${severity.toLowerCase()}`;
  const getStatusClass = (status: string) => `status-${status.toLowerCase()}`;
  const getPriorityClass = (priority: string) => `priority-${(priority || 'p3').toLowerCase()}`;
  const safeTimeline = Array.isArray((alert as any).timeline) ? (alert as any).timeline : [];

  const externalTicketId = String((alert as any).externalTicketId || '').trim();
  const latestWorkflowTriggerAt = safeTimeline
    .filter((evt: any) => String(evt?.type || '').toLowerCase() === 'workflowtriggered')
    .map((evt: any) => new Date(evt?.timestamp || 0).getTime())
    .filter((ms: number) => !Number.isNaN(ms) && ms > 0)
    .sort((a: number, b: number) => b - a)[0] ?? 0;

  const hasTicketCreatedEvent = safeTimeline.some((evt: any) => {
    const type = String(evt?.type || '').toLowerCase();
    const desc = String(evt?.description || '').toLowerCase();
    return type === 'ticketcreated' || desc.includes('external ticket created');
  });

  const ticketRequestPending = !externalTicketId
    && String(alert.status || '').toLowerCase() !== 'closed'
    && latestWorkflowTriggerAt > 0
    && !hasTicketCreatedEvent
    && (Date.now() - latestWorkflowTriggerAt) <= 180000;

  const resolveClosedDisplay = () => {
    if (alert.closedAt) return formatDateTime(alert.closedAt);

    const timelineEvents = (Array.isArray((alert as any).timeline) ? (alert as any).timeline : []) as any[];
    const legacyEvents = (Array.isArray((alert as any).events) ? (alert as any).events : []) as any[];

    const closedEvent = [...timelineEvents, ...legacyEvents]
      .filter((e: any) => String(e?.type || '').toLowerCase() === 'closed')
      .sort((a: any, b: any) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime())[0];

    if (closedEvent?.timestamp) return formatDateTime(closedEvent.timestamp);

    return 'Closed';
  };

  const formatCountdown = (secs: number): string => {
    if (secs <= 0) return '0:00';
    const m = Math.floor(secs / 60);
    const s = secs % 60;
    return `${m}:${s.toString().padStart(2, '0')}`;
  };

  const priorities = ['P1', 'P2', 'P3', 'P4', 'P5'];

  const formatTimelineType = (value: string): string => {
    const raw = String(value || '').trim();
    if (!raw) return 'EVENT';
    return raw
      .replace(/[_-]+/g, ' ')
      .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
      .replace(/\s+/g, ' ')
      .trim()
      .toUpperCase();
  };

  const timelineTypeClass = (value: string): string => {
    const normalized = String(value || '').trim().toLowerCase();
    if (normalized === 'ack') return 'acknowledged';
    return normalized;
  };

  const formatTimelineTimestamp = (value: string): string => {
    return formatDateTime(value, { year: 'numeric', month: 'short', day: '2-digit', hour: 'numeric', minute: '2-digit', timeZoneName: 'short' });
  };

  const localizeDescriptionDateTimes = (description: unknown): string => {
    if (description == null) return '';
    const text = typeof description === 'string'
      ? description
      : (() => {
          try {
            return JSON.stringify(description, null, 2);
          } catch {
            return String(description);
          }
        })();
    return text.replace(/Date\/Time:\s*([^\n\r]+)/gi, (_full, rawValue: string) => {
      const source = String(rawValue || '').trim();
      const parsed = new Date(source);
      if (Number.isNaN(parsed.getTime())) {
        const utcMatch = source.match(/^(\d{4}-\d{2}-\d{2})\s+(\d{2}:\d{2}:\d{2})\s+UTC$/i);
        if (!utcMatch) return `Date/Time: ${source}`;
        const iso = `${utcMatch[1]}T${utcMatch[2]}Z`;
        const reparsed = new Date(iso);
        if (Number.isNaN(reparsed.getTime())) return `Date/Time: ${source}`;
        return `Date/Time: ${formatTimelineTimestamp(reparsed.toISOString())}`;
      }
      return `Date/Time: ${formatTimelineTimestamp(parsed.toISOString())}`;
    });
  };

  return (
    <div className="alert-detail" ref={detailRef}>
      <header className="detail-header">
        <h2>Alert Details</h2>
        <button className="btn-close" onClick={onClosePanel}>
          ×
        </button>
      </header>

      <div className="detail-content">
        <div className="alert-meta">
          <span className={`badge severity ${getSeverityClass(alert.severity)}`}>
            {alert.severity}
          </span>
          <span className={`badge status ${getStatusClass(alert.status)}`}>
            {alert.status}
          </span>
          {alert.priority && (
            <span
              className={`badge priority ${getPriorityClass(alert.priority)} ${onChangePriority ? 'clickable' : ''}`}
              onClick={() => onChangePriority && setShowPriorityPicker(!showPriorityPicker)}
              title={onChangePriority ? 'Click to change priority' : undefined}
            >
              {alert.priority}
            </span>
          )}
          {showPriorityPicker && (
            <div className="priority-picker">
              {priorities.map((p) => (
                <button
                  key={p}
                  className={`priority-option ${getPriorityClass(p)} ${p === alert.priority ? 'current' : ''}`}
                  onClick={() => handlePriorityChange(p)}
                  disabled={p === alert.priority}
                >
                  {p}
                </button>
              ))}
            </div>
          )}
        </div>

        <h3 className="alert-title">{alert.title}</h3>

        <div className="raw-payload-section">
          <button
            className="btn-view-payload"
            onClick={() => setShowRawPayload(true)}
          >
            Show Alert Raw Data
          </button>
          {showRawPayload && (
            <div className="payload-modal-overlay" onClick={() => setShowRawPayload(false)}>
              <div className="payload-modal" onClick={(e) => e.stopPropagation()}>
                <h4>Raw Payload</h4>
                <pre>{(alert as any).rawPayload || 'Raw payload is not available for this alert.'}</pre>
                <button className="btn-close-modal" onClick={() => setShowRawPayload(false)}>
                  Close
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Escalation Info Panel */}
        {alert.escalationInfo && !alert.escalationInfo.isComplete && (
          <div className="escalation-info-panel">
            <h4>Escalation Status</h4>
            <div className="escalation-details">
              <div className="escalation-row">
                <span className="label">Policy:</span>
                <span className="value">{alert.escalationInfo.policyName}</span>
              </div>
              <div className="escalation-row">
                <span className="label">Tier:</span>
                <span className="value">{alert.escalationInfo.currentTier} of {alert.escalationInfo.totalTiers}</span>
              </div>
              <div className="escalation-row">
                <span className="label">Mode:</span>
                <span className="value escalation-mode">{alert.escalationInfo.escalationMode}</span>
              </div>
              {secondsRemaining != null && secondsRemaining > 0 && (
                <div className="escalation-countdown">
                  <div className="countdown-label">Next escalation in</div>
                  <div className={`countdown-value ${secondsRemaining < 60 ? 'countdown-urgent' : ''}`}>
                    {formatCountdown(secondsRemaining)}
                  </div>
                </div>
              )}
              {secondsRemaining != null && secondsRemaining <= 0 && (
                <div className="escalation-countdown">
                  <div className="countdown-label">Escalation</div>
                  <div className="countdown-value countdown-expired">Overdue</div>
                </div>
              )}
              {alert.escalationInfo.nextAction && (
                <div className="escalation-next-action">
                  <span className="label">Next:</span>
                  <span className="value">{alert.escalationInfo.nextAction}</span>
                </div>
              )}
              {alert.escalationInfo.fallbackEnabled && (
                <div className="escalation-fallback-badge">Fallback enabled</div>
              )}
            </div>
          </div>
        )}

        {alert.escalationInfo?.isComplete && (
          <div className="escalation-info-panel escalation-complete">
            <h4>Escalation Complete</h4>
            <div className="escalation-details">
              <div className="escalation-row">
                <span className="label">Policy:</span>
                <span className="value">{alert.escalationInfo.policyName}</span>
              </div>
              <div className="escalation-row">
                <span className="label">Final tier reached:</span>
                <span className="value">{alert.escalationInfo.currentTier} of {alert.escalationInfo.totalTiers}</span>
              </div>
            </div>
          </div>
        )}

        <div className="alert-info">
          <div className="info-row">
            <span className="label">Service:</span>
            <span className="value">{alert.service}</span>
          </div>
          <div className="info-row">
            <span className="label">Source:</span>
            <span className="value">{alert.source}</span>
          </div>
          {externalTicketId && (
            <div className="info-row">
              <span className="label">Ticket #:</span>
              <span className="value ticket-id" title={externalTicketId}>{externalTicketId}</span>
            </div>
          )}
          {ticketRequestPending && (
            <div className="info-row ticket-pending-row">
              <span className="label">Ticket Request:</span>
              <span className="value">Waiting for helpdesk response...</span>
            </div>
          )}
          <div className="info-row">
            <span className="label">Created:</span>
            <span className="value">{formatDateTime(alert.createdAt)}</span>
          </div>
          {(alert as any).correlationId && (
            <div className="info-row">
              <span className="label">Correlation ID:</span>
              <span className="value">{(alert as any).correlationId}</span>
            </div>
          )}
          {alert.ackBy && (
            <div className="info-row">
              <span className="label">Acknowledged:</span>
              <span className="value">
                {alert.ackAt && formatDateTime(alert.ackAt)}
              </span>
            </div>
          )}
          {(alert.status === 'Closed' || alert.closedAt) && (
            <div className="info-row">
              <span className="label">Closed:</span>
              <span className="value">
                {resolveClosedDisplay()}
              </span>
            </div>
          )}
          {(alert as any).assignedTo && (
            <div className="info-row">
              <span className="label">Assigned to:</span>
              <span className="value">
                {users?.find((u) => u.userId === (alert as any).assignedTo)?.displayName || (alert as any).assignedTo}
              </span>
            </div>
          )}
          <div className="info-row">
            <span className="label">Updated:</span>
            <span className="value">{formatDateTime(alert.updatedAt)}</span>
          </div>
        </div>

        {alert.description && (
          <div className="alert-description">
            <h4>Description</h4>
            <p>{alert.description}</p>
          </div>
        )}

        <div className="alert-actions">
          {alert.status === 'Closed' && onReopen && (
            <button className="btn-assign" onClick={handleReopen} disabled={reopening}>
              {reopening ? 'Reopening...' : 'Reopen Alert'}
            </button>
          )}
          {reopenError && <p role="alert">{reopenError}</p>}
          {alert.status === 'Open' && (
            <button className="btn-acknowledge" onClick={onAcknowledge}>
              Acknowledge
            </button>
          )}
          {alert.status !== 'Closed' && onAssign && users && users.length > 0 && (
            <div className="assign-section">
              <button
                className="btn-acknowledge btn-assign"
                onClick={() => setShowAssignPicker(!showAssignPicker)}
              >
                Assign
              </button>
              {showAssignPicker && (
                <div className="assign-picker">
                  {users.map((u) => (
                    <button
                      key={u.userId}
                      className={`assign-option ${(alert as any).assignedTo === u.userId ? 'current' : ''}`}
                      onClick={() => {
                        setShowAssignPicker(false);
                        onAssign(u.userId);
                      }}
                    >
                      {u.displayName || u.email}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
          {alert.status !== 'Closed' && (
            <>
              <textarea
                placeholder="Resolution note (optional)"
                value={resolutionNote}
                onChange={(e) => setResolutionNote(e.target.value)}
                rows={2}
              />
              <button className="btn-close-alert" onClick={handleClose}>
                Close Alert
              </button>
            </>
          )}
        </div>

        {loadWorkflowProgress && (
          <WorkflowProgressPanel alertId={alert.alertId} loadProgress={loadWorkflowProgress}
            subscribe={subscribeWorkflowProgress} refreshKey={`${alert.status}|${safeTimeline.length}`} />
        )}

        <div className="timeline-section">
          <h4>Timeline</h4>
          <div className="timeline">
            {safeTimeline.map((event: any, index: number) => {
              const item = event && typeof event === 'object' ? event : {};
              const payload = formatEventPayload(item.rawDetails);
              const imageArtifact = getWebhookImageArtifact(item.type, item.rawDetails);
              return (
                <div key={index} className={`timeline-event ${timelineTypeClass(item.type)}`}>
                  <div className="timeline-dot" />
                  <div className="timeline-content">
                    <div className="timeline-heading">
                      <div className="timeline-heading-labels">
                        <span className="timeline-type">{formatTimelineType(item.type)}</span>
                        <span className="timeline-time">{formatTimelineTimestamp(item.timestamp)}</span>
                      </div>
                      {payload !== null && (
                        <button type="button" className="timeline-payload-button" aria-haspopup="dialog"
                          aria-label={imageArtifact ? `View image artifact ${imageArtifact.artifactName}` : undefined}
                          onClick={event => setEventPayload({ payload, heading: formatTimelineType(item.type), trigger: event.currentTarget, imageArtifact: imageArtifact ?? undefined })}>
                          {imageArtifact ? 'View Image' : 'View Payload'}
                        </button>
                      )}
                    </div>
                    <p className="timeline-description">{localizeDescriptionDateTimes(item.description)}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="add-note-section">
          <h4>Add Note</h4>
          <textarea
            placeholder="Enter your note..."
            value={note}
            onChange={(e) => setNote(e.target.value)}
            rows={3}
          />
          <button
            className="btn-add-note"
            onClick={handleAddNote}
            disabled={!note.trim()}
          >
            Add Note
          </button>
        </div>
      </div>
      {eventPayload && <EventPayloadModal payload={eventPayload.payload} eventHeading={eventPayload.heading}
        trigger={eventPayload.trigger} imageArtifact={eventPayload.imageArtifact ?? undefined}
        onClose={() => setEventPayload(null)} />}
    </div>
  );
};
